import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import Layout from '../components/Layout';
import { setCrumb, clearCrumb } from '../context/ShellCrumbContext';
import { useAuth } from '../context/AuthContext';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import {
  getHandoverInspection,
  getHandoverInspectionAttendance,
  listHandoverInspections,
  getAssetQATable,
  getProjectUsers,
  addHandoverAttendee,
  updateHandoverAttendee,
  signMyHandoverAttendance,
  recordHandoverDidNotSign,
  completeHandoverInspection,
  cancelHandoverInspection,
} from '../lib/api';
import {
  CLASSIFICATION_LABEL, DID_NOT_SIGN_LIMIT_PASSED, RECEIVER_REASON_LABEL, UNASSIGNED, WHY_RECEIVER_LIMIT_PASSED,
  attendanceStatusView, blockerText, completeRefusalText, hiHistory, hiLifeNote, hoAt, hoDay, hoStamp, inspectionKindTitle,
  inspectionStatusLabel, isPunchOpen, isQaCloseout, isUnresolved, lateLabel, noticeGivenText, openByCategory,
  punchStateLabel, receiverLimitPassedText, receiverNotSignedByCompany, receiverNotSignedRecordText,
  receivingCompanyName, scheduledInstant, scheduledText, serverMessage, waitingForSignatureText,
} from '../lib/handoverView';
import {
  Alert, CatChip, Chip, Facts, HoDialog, Lifecycle, Pill, Section, btn, why,
} from '../components/handover/v2/HoBits';
import RaisePunchDialog from '../components/handover/v2/RaisePunchDialog';
import ResizableTable, { ResizableTh, TD_CLASS, TD_NOWRAP } from '../components/ui/ResizableTable';

/**
 * H-FE-1 B1 — THE HANDOVER INSPECTION (`prototipo-handover-v2.html`, `hiPage`).
 *
 * Draws `GET /handover-inspections/{id}` for this requester: the lifecycle
 * (Booked · Inspection · Completed) with its line, Inspection details, the
 * attendance, the assets on the inspection, the punch items raised on it and
 * its history. Every button is a served gate (`actions.*`); every refusal is
 * the server's sentence (`detail.message`).
 *
 * H-FE-2 B1 — signing declares presence (H-BE-GAPS-1): every person signs
 * through their own door (`POST .../attendance/sign`), an invitee nobody has
 * recorded included, and the button follows `actions.can_sign`.
 *
 * H-FE-3 (§19) — once the receiving company's time to sign has passed
 * (`actions.why === "receiver_signature_limit_passed"`), the completion asks
 * why and sends it as `receiver_not_signed_reason`; the record says so.
 *
 * FIX-FE-1 — every date and time on the page reads on the project's clock, the
 * served `project_timezone` of `GET /handover-inspections/{id}`. An answer
 * without it reads as before.
 */

const SIGN_NOTE = 'Signing says “I was here”. Each person signs in their own login; the organiser only records who did not attend, who attended without signing, and people from outside.';

const attendeeCompany = (a, usersById) => a?.company_name
  || usersById.get(a?.user_id)?.company_name || '';

function AttendanceStatus({ a, tz }) {
  // DOSSIER-FE-1 B0 — the words live in lib/handoverView (one source with the dossier).
  const { cls, text, sub } = attendanceStatusView(a, tz);
  return (
    <span className={`ml-auto text-[12px] text-right ${cls}`}>
      {text}
      {sub ? <span className="block text-slate-500 text-[11px]">{sub}</span> : null}
    </span>
  );
}

/** The minimum view: a present attendee outside the package's fence signs from here. */
function MinimalAttendance({ view, onSign, busy }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-testid="hi-minimal">
      <div className="px-4 pt-3.5 pb-2.5">
        <div className="font-mono text-[11.5px] text-orange-500 tracking-wide">{view.document_id}</div>
        <h2 className="m-0 mt-0.5 text-[22px] text-slate-50">{view.title || 'Inspection'}</h2>
        <div className="text-[12.5px] text-slate-400">{scheduledText(view, view.project_timezone)}</div>
      </div>
      <Section title="Attendance" testId="hi-minimal-attendance">
        {(view.attendees || []).map((a) => (
          <div key={a.id || a.user_id} className="flex gap-2.5 items-center bg-[#0d1628] border border-slate-800 rounded-lg px-3 py-2 mb-1.5 flex-wrap">
            <span className="font-bold text-slate-50 text-[13px]">{a.name}</span>
            <span className="text-slate-400 text-[12px]">{a.company_name || ''}</span>
            <AttendanceStatus a={a} tz={view.project_timezone} />
          </div>
        ))}
        {view.actions?.can_sign ? (
          <button type="button" className={btn.primary} disabled={busy} data-testid="hi-minimal-sign"
            onClick={onSign}>Sign my attendance</button>
        ) : null}
        <p className={`${why} mt-2`}>{SIGN_NOTE}</p>
      </Section>
    </div>
  );
}

export default function HandoverInspectionPage() {
  const { projectId, inspectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canCreate, isClientRep, isLoading: permsLoading } = useProjectPermissions(projectId);

  const [hi, setHi] = useState(null);
  const [minimal, setMinimal] = useState(null);
  const [row, setRow] = useState({});
  const [qaRows, setQaRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const doc = await getHandoverInspection(inspectionId);
      setHi(doc);
      setMinimal(null);
      setError(null);
    } catch (err) {
      if (err?.response?.status === 404) {
        try {
          const view = await getHandoverInspectionAttendance(inspectionId);
          setMinimal(view);
          setHi(null);
          return;
        } catch {
          setError('That inspection is not on this project, or you cannot see it.');
          return;
        }
      }
      setError(serverMessage(err, 'Could not load the inspection.'));
      return;
    }
    // The facts GET /{id} does not serve (who booked, notice given, when it was
    // cancelled or completed) come off the project's list row; the discipline
    // and asset group off the asset QA table; people's companies off the users.
    const [list, qa, people] = await Promise.all([
      Promise.resolve().then(() => listHandoverInspections(projectId)).catch(() => null),
      Promise.resolve().then(() => getAssetQATable(projectId)).catch(() => null),
      Promise.resolve().then(() => getProjectUsers(projectId)).catch(() => null),
    ]);
    setRow(((list && list.inspections) || []).find((i) => i.id === inspectionId) || {});
    setQaRows(Array.isArray(qa?.data) ? qa.data : []);
    setUsers(Array.isArray(people?.data) ? people.data : []);
  }, [inspectionId, projectId]);

  useEffect(() => { load(); }, [load]);

  // NAV-FE-1c — the shell's breadcrumb carries this page's path:
  // `Handover › <document>`; `Handover` is the way back.
  const crumbDoc = hi && !error ? (hi.document_id || inspectionKindTitle(hi)) : '';
  useEffect(() => {
    setCrumb([{ label: 'Handover', to: `/projects/${projectId}/handover` }, { label: crumbDoc }]);
    return () => clearCrumb();
  }, [projectId, crumbDoc]);

  const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const qaById = useMemo(() => new Map(qaRows.map((r) => [r.element_id, r])), [qaRows]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const res = await fn();
      if (ok) toast.success(typeof ok === 'function' ? ok(res) : ok);
      await load();
      return true;
    } catch (err) {
      toast.error(serverMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <Layout>
        <div className="max-w-[1360px] mx-auto px-4 md:px-6 py-5">
          <div className="text-center text-slate-500 py-9 text-[13px]" data-testid="hi-error">{error}</div>
        </div>
      </Layout>
    );
  }

  if (minimal) {
    return (
      <Layout>
        <div className="max-w-[1360px] mx-auto px-4 md:px-6 py-5 pb-24">
          <MinimalAttendance
            view={minimal}
            busy={busy}
            onSign={() => run(() => signMyHandoverAttendance(inspectionId), 'Signed')}
          />
        </div>
      </Layout>
    );
  }

  if (!hi) {
    return (
      <Layout>
        <div className="flex justify-center py-16" data-testid="hi-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      </Layout>
    );
  }

  const now = new Date();
  // FIX-FE-1 — the clock of every date and time below: the project's, as served.
  const tz = hi.project_timezone;
  const qc = isQaCloseout(hi);
  const actions = hi.actions || {};
  const scheduled = hi.status === 'scheduled';
  const at = scheduledInstant(hi);
  const status = inspectionStatusLabel(hi, now);
  const attendees = hi.attendees || [];
  const me = user?.id;
  const myLine = attendees.find((a) => a.kind === 'user' && a.user_id === me) || null;
  const canManage = !!actions.can_manage_attendance;
  const anySigned = attendees.some((a) => a.signed_at);
  const fromName = hi.handover_from_project_company_name || UNASSIGNED;
  const toName = hi.handover_to_project_company_name || UNASSIGNED;
  const rc = receivingCompanyName(hi);
  const companyOf = (a) => attendeeCompany(a, usersById);
  const myCompanyId = usersById.get(me)?.default_project_company_id || null;
  const parties = qc ? [hi.handover_from_project_company_id]
    : [hi.handover_from_project_company_id, hi.handover_to_project_company_id];
  const canRaise = !permsLoading && hi.status !== 'cancelled' && (canCreate || isClientRep)
    && (!myCompanyId || parties.includes(myCompanyId));

  const assets = hi.assets || [];
  const hiItems = [];
  const seen = new Set();
  assets.forEach((a) => (a.punch_items || []).forEach((p) => {
    if (p.inspection_id === (hi.inspection_id || inspectionId) && !seen.has(p.id)) {
      seen.add(p.id);
      hiItems.push(p);
    }
  }));
  hiItems.sort((x, y) => (x.punch_seq || 0) - (y.punch_seq || 0));
  const openA = (a) => (a.punch_items || []).filter((p) => p.category === 'A' && isPunchOpen(p)).length;

  const markPresentFor = async (a) => {
    const served = a.id
      ? await updateHandoverAttendee(inspectionId, a.id, { present: true })
      : await addHandoverAttendee(inspectionId, { user_id: a.user_id, present: true });
    return (served?.attendees || []).find((x) => x.kind === 'user' && x.user_id === a.user_id) || null;
  };

  const openDidNotAttend = (a) => setDialog({
    title: 'Did not attend',
    sub: `${a.name} · ${companyOf(a) || ''} — recorded by the organiser`,
    okLabel: 'Record',
    fields: a.id && anySigned ? [{ k: 'reason', label: 'Why', type: 'textarea', req: true }] : [],
    onSubmit: async (v) => {
      if (a.id) await updateHandoverAttendee(inspectionId, a.id, { present: false, ...(v.reason ? { reason: v.reason } : {}) });
      else await addHandoverAttendee(inspectionId, { user_id: a.user_id, present: false });
      setDialog(null);
      toast.success('Recorded');
      await load();
    },
  });

  const openDidNotSign = (a) => setDialog({
    title: 'Attended, did not sign',
    sub: `${a.name} · ${companyOf(a) || ''} — recorded by the organiser, with a reason`,
    okLabel: 'Record',
    fields: [{ k: 'reason', label: 'Why', type: 'textarea', req: true }],
    onSubmit: async (v) => {
      const line = a.state === 'invited' ? await markPresentFor(a) : a;
      await recordHandoverDidNotSign(inspectionId, line?.id || a.id, v.reason.trim());
      setDialog(null);
      toast.success('Recorded');
      await load();
    },
  });

  const openAddExternal = () => setDialog({
    title: 'Add external attendee',
    sub: 'Someone without a ConstructQA login. The organiser records that they attended.',
    okLabel: 'Add',
    fields: [
      { k: 'name', label: 'Name', req: true, placeholder: 'e.g., Kim Lee' },
      { k: 'co', label: 'Company', req: true, placeholder: 'e.g., Designer D' },
    ],
    onSubmit: async (v) => {
      await addHandoverAttendee(inspectionId, { external: { name: v.name.trim(), company: v.co.trim() }, present: true });
      setDialog(null);
      toast.success('Attendee added');
      await load();
    },
  });

  const openCancel = () => setDialog({
    title: `Cancel ${hi.document_id || 'this inspection'}`,
    sub: 'Stays on the record as cancelled. The package goes back to needing an inspection.',
    okLabel: 'Cancel inspection',
    danger: true,
    fields: [],
    onSubmit: async () => {
      await cancelHandoverInspection(inspectionId);
      setDialog(null);
      toast.success('Cancelled');
      await load();
    },
  });

  const openComplete = () => {
    const acc = assets.filter((a) => !openA(a)).length;
    const rest = assets.length - acc;
    const signedNames = attendees.filter((a) => a.signed_at).map((a) => a.name);
    const others = attendees.filter((a) => a.user_id !== me && isUnresolved(a));
    // H-FE-2 B2 — the invitees nobody has recorded (served `state: "invited"`):
    // the request closes their attendance with one outcome each. The organiser
    // is never listed: the request itself signs them (H-BE-GAPS-1 B2).
    const invitedOnly = others.filter((a) => a.state === 'invited' && a.user_id);
    // H-FE-3 (§19) — the receiving company's time to sign has passed: the
    // request carries a reason, and the server records its present, unsigned
    // attendees "did not sign" with it (shown, never chosen here).
    const pastLimit = actions.why === WHY_RECEIVER_LIMIT_PASSED;
    const limitCo = hi.waiting_for_signature?.company_name || rc;
    const presentUnsigned = others.filter((a) => a.state === 'awaiting_signature');
    const locked = pastLimit ? presentUnsigned.filter((a) => a.classification === 'receiving') : [];
    const waiting = presentUnsigned.filter((a) => !locked.includes(a));
    const mineCounts = canManage || myLine?.state === 'awaiting_signature' || !!myLine?.signed_at;
    const limitFields = pastLimit ? [
      {
        k: 'limit_block',
        type: 'static',
        block: true,
        node: <Alert tone="warn" testId="hi-complete-limit">{receiverLimitPassedText(limitCo, hi.waiting_for_signature?.limit, tz)}</Alert>,
      },
      { k: 'receiver_reason', label: RECEIVER_REASON_LABEL, type: 'textarea', req: true },
    ] : [];
    const lockedNode = locked.length ? (
      <div data-testid="hi-complete-locked">
        {locked.map((a) => (
          <div key={a.user_id} className="flex gap-2.5 items-baseline flex-wrap py-0.5" data-testid={`hi-locked-${a.user_id}`}>
            <b className="text-slate-50 text-[13px]">{[a.name, companyOf(a)].filter(Boolean).join(' · ')}</b>
            <span className="text-red-400 text-[12px]">{DID_NOT_SIGN_LIMIT_PASSED}</span>
          </div>
        ))}
      </div>
    ) : null;
    const outcomeFields = invitedOnly.length || locked.length ? [
      { k: 'not_signed', label: 'Not signed', type: 'static', block: true, node: lockedNode },
      ...invitedOnly.flatMap((a) => [
        {
          k: `o_${a.user_id}`,
          label: [a.name, companyOf(a)].filter(Boolean).join(' · '),
          type: 'radios',
          options: [
            { v: 'did_not_attend', l: 'Did not attend', on: true },
            { v: 'did_not_sign', l: 'Attended, did not sign' },
          ],
        },
        {
          k: `r_${a.user_id}`, label: 'Why', type: 'textarea', req: true, show: (v) => v[`o_${a.user_id}`] === 'did_not_sign',
        },
      ]),
    ] : [];
    const body = (
      <div className="text-[13px] text-slate-300" data-testid="hi-complete-body">
        {qc
          ? <p className="m-0">Completing records the closeout of {assets.length} assets by {fromName}.</p>
          : (
            <p className="m-0">
              Completing this inspection hands over {acc} asset{acc === 1 ? '' : 's'} to {toName}.
              {rest ? ` ${rest} will remain in rectification until the Category A items are approved.` : ''}
            </p>
          )}
        {!qc ? (
          <div className="mt-3">
            <span className="text-emerald-400 block" data-testid="hi-complete-accepted">{acc} — Handover accepted</span>
            <span className="text-slate-500 text-[11.5px] block">No Category A item open.</span>
            {rest ? (
              <>
                <span className="text-red-400 block mt-1" data-testid="hi-complete-rect">{rest} — Rectification required — Category A open</span>
                <span className="text-slate-500 text-[11.5px] block">Open Category A items block Handover Completed.</span>
              </>
            ) : null}
          </div>
        ) : null}
        <p className="mt-3 mb-0 text-slate-400">
          {`Signed: ${signedNames.join(', ') || 'nobody yet'}.`}
          {mineCounts ? ' Completing counts as your own signature.' : ''}
        </p>
      </div>
    );
    const note = (
      <div className="text-[13px] text-slate-400" data-testid="hi-complete-note">
        {waiting.length ? (
          <p className="mt-0 mb-0">
            {`Waiting for: ${waiting.map((a) => a.name).join(', ')} — present and not signed; the inspection completes once each signs or you record that they did not sign.`}
          </p>
        ) : null}
        {pastLimit ? null : (
          <p className="mt-2 mb-0 text-slate-500 text-[12px]">
            {`The inspection completes once every present attendee is resolved and someone from ${rc} has signed.`}
          </p>
        )}
      </div>
    );
    setDialog({
      title: qc ? 'Complete the QA Closeout Inspection?' : 'Complete the Handover Inspection?',
      okLabel: pastLimit ? 'Complete without their signature' : 'Complete inspection',
      okTone: 'ok',
      fields: [...limitFields, { k: 'summary', type: 'static', block: true, node: body }, ...outcomeFields],
      children: note,
      refusal: (err) => completeRefusalText(err, limitCo, tz),
      onSubmit: async (v) => {
        const outcomes = invitedOnly.map((a) => (v[`o_${a.user_id}`] === 'did_not_sign'
          ? { user_id: a.user_id, outcome: 'did_not_sign', reason: String(v[`r_${a.user_id}`] || '').trim() }
          : { user_id: a.user_id, outcome: 'did_not_attend' }));
        const res = await completeHandoverInspection(inspectionId, pastLimit
          ? { outcomes, receiver_not_signed_reason: String(v.receiver_reason || '').trim() }
          : { outcomes });
        setDialog(null);
        toast.success(res?.status === 'completed' || res?.completion?.completed ? 'Completed'
          : 'Completion requested — it completes once everyone present is resolved');
        await load();
      },
    });
  };

  const openRaise = (preAsset = null) => setDialog({
    raise: true, preAsset,
  });

  // ── lifecycle ───────────────────────────────────────────────────────────
  const steps = [
    { lab: 'Booked', who: fromName },
    { lab: 'Inspection', who: 'on the day: walk the assets, raise punch items, each person signs' },
    { lab: 'Completed', who: `the organiser closes it once ${rc} has signed` },
  ];
  const lifeNote = hiLifeNote(hi, companyOf, now);
  const waitingText = scheduled ? waitingForSignatureText(hi.waiting_for_signature, tz) : '';
  const notSignedBy = receiverNotSignedByCompany(hi, (uid) => usersById.get(uid)?.company_name);
  const notSignedText = receiverNotSignedRecordText(hi, notSignedBy);
  const history = hiHistory(hi, row, notSignedBy);
  const blockers = hi.completion?.blockers || [];

  return (
    <Layout>
      <div className="max-w-[1360px] mx-auto px-4 md:px-6 py-5 pb-24" data-testid="hi-page">
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          {/* header */}
          <div className="flex gap-3.5 items-start flex-wrap px-4 pt-3.5 pb-2.5">
            <div className="min-w-0">
              <div className="font-mono text-[11.5px] text-orange-500 tracking-wide" data-testid="hi-docid">{hi.document_id}</div>
              <h2 className="m-0 mt-0.5 mb-0.5 text-[22px] text-slate-50" data-testid="hi-title">{inspectionKindTitle(hi)}</h2>
              <div className="text-[12.5px] text-slate-400" data-testid="hi-meta">
                {[scheduledText(hi, tz), `${assets.length} asset${assets.length === 1 ? '' : 's'}`, hi.title].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className="ml-auto flex gap-2 items-center flex-wrap">
              <Pill tone={hi.status === 'completed' ? 'ok' : undefined} testId="hi-status">{status}</Pill>
              {hi.completion?.requested && scheduled ? <Chip tone="flag" testId="hi-requested">Completion requested</Chip> : null}
              {myLine?.state === 'awaiting_signature' && scheduled ? <Chip tone="mine" testId="hi-your-signature">Your signature</Chip> : null}
              {actions.can_complete && !hi.completion?.requested ? (
                <button type="button" className={btn.ok} disabled={busy} data-testid="hi-complete-btn" onClick={openComplete}>✓ Complete inspection</button>
              ) : null}
              {!actions.can_complete && actions.why === 'receiver_not_signed' && !hi.completion?.requested ? (
                <>
                  <button type="button" className={btn.ok} disabled data-testid="hi-complete-btn">✓ Complete inspection</button>
                  <span className="text-[11.5px] text-amber-400" data-testid="hi-complete-why">{`Nobody from ${rc} has signed yet`}</span>
                </>
              ) : null}
              {actions.can_cancel && at && now < at ? (
                <button type="button" className={btn.ghost} disabled={busy} data-testid="hi-cancel-btn" onClick={openCancel}>Cancel</button>
              ) : null}
            </div>
          </div>

          {/* lifecycle */}
          <div className="px-4 pt-1.5 pb-2 border-t border-slate-800">
            <Lifecycle
              steps={steps}
              cur={hi.status === 'completed' ? 3 : 1}
              allDone={hi.status === 'completed'}
              off={hi.status === 'cancelled'}
              note={lifeNote}
              testId="hi-life"
            />
            {waitingText ? (
              <p className="text-[11.5px] text-amber-400 mt-1 mb-0" data-testid="hi-waiting">{waitingText}</p>
            ) : null}
            {notSignedText ? (
              <p className="text-[11.5px] text-amber-400 mt-1 mb-0" data-testid="hi-receiver-not-signed">{notSignedText}</p>
            ) : null}
          </div>

          <Section title="Inspection details" testId="hi-details">
            <Facts
              testId="hi-facts"
              items={[
                { label: 'Document ID', value: <span className="font-mono text-orange-500">{hi.document_id || '—'}</span> },
                { label: 'Date & time', value: scheduledText(hi, tz) || '—' },
                {
                  label: 'Booked by',
                  value: row.created_by_name || '—',
                  sub: [row.created_at ? hoDay(row.created_at, tz) : '', qc ? '' : noticeGivenText(row.notice_given)].filter(Boolean).join(' · '),
                  testId: 'hi-booked-by',
                },
                { label: 'Handover from', value: fromName, testId: 'hi-from' },
                { label: qc ? 'Receiving' : 'Handover to', value: qc ? '— (QA Closeout)' : toName, testId: 'hi-to' },
                { label: 'Assets', value: String(assets.length) },
              ]}
            />
            {hi.status === 'cancelled' ? (
              <Alert tone="warn" className="mt-3" testId="hi-alert-cancelled">
                Cancelled{row.cancelled_by_name ? ` by ${row.cancelled_by_name}` : ''}{row.cancelled_at ? ` · ${hoDay(row.cancelled_at, tz)}` : ''}. The package goes back to needing an inspection.
              </Alert>
            ) : null}
            {hi.status === 'completed' ? (
              <Alert tone="ok" className="mt-3" testId="hi-alert-completed">
                Completed on {hoAt(hi.completion?.completed_at || row.completed_at, tz)}. {qc
                  ? 'QA Complete follows the punch rule of the QMP.'
                  : 'Handover Completed per asset follows: no Category A item may stay open.'}
              </Alert>
            ) : null}
            {hi.completion?.held ? (
              <Alert tone="warn" className="mt-3" testId="hi-alert-held">
                {hi.completion.held.company?.name || 'A company'} has not approved the QMP rules for this package yet. Asking to complete waits until it does.
              </Alert>
            ) : null}
            {hi.completion?.requested && scheduled && blockers.length ? (
              <Alert tone="info" className="mt-3" testId="hi-alert-waiting">
                <b className="text-slate-50">Completion requested.</b> {blockers.map((b) => blockerText(b, hi)).join(' ')}
              </Alert>
            ) : null}
          </Section>

          <Section
            title="Attendance"
            testId="hi-attendance"
            count={`${attendees.filter((a) => a.signed_at).length} signed · ${attendees.filter(isUnresolved).length} not yet`}
            right={canManage ? (
              <button type="button" className={`${btn.ghost} ${btn.sm}`} disabled={busy} data-testid="hi-add-external" onClick={openAddExternal}>Add external attendee</button>
            ) : null}
          >
            {attendees.map((a) => {
              const mine = a.kind === 'user' && a.user_id === me;
              const live = scheduled && isUnresolved(a);
              const cls = CLASSIFICATION_LABEL[a.classification];
              const key = a.id || `inv-${a.user_id}`;
              return (
                <div key={key} className="flex gap-2.5 items-center bg-[#0d1628] border border-slate-800 rounded-lg px-3 py-2 mb-1.5 flex-wrap" data-testid={`hi-att-${a.user_id || a.id}`}>
                  <span className="font-bold text-slate-50 text-[13px]">{a.name}</span>
                  <span className="text-slate-400 text-[12px]">
                    {a.kind === 'external' ? `${a.company_name || ''} · external` : (companyOf(a) || '—')}
                    {cls ? ` · ${cls}` : ''}
                  </span>
                  <AttendanceStatus a={a} tz={tz} />
                  {mine && actions.can_sign ? (
                    <span className="flex gap-1.5 items-center ml-2 flex-wrap">
                      <button type="button" className={`${btn.primary} ${btn.sm}`} disabled={busy} data-testid="hi-sign-mine"
                        onClick={() => run(() => signMyHandoverAttendance(inspectionId), 'Signed')}>
                        Sign my attendance
                      </button>
                    </span>
                  ) : null}
                  {live && !mine && canManage ? (
                    <span className="flex gap-1.5 ml-2 flex-wrap">
                      <button type="button" className={`${btn.ghost} ${btn.sm}`} disabled={busy} data-testid={`hi-absent-${a.user_id}`}
                        onClick={() => openDidNotAttend(a)}>Did not attend</button>
                      {a.state === 'invited' || actions.can_record_did_not_sign ? (
                        <button type="button" className={`${btn.ghost} ${btn.sm}`} disabled={busy} data-testid={`hi-dns-${a.user_id}`}
                          onClick={() => openDidNotSign(a)}>Attended, did not sign</button>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              );
            })}
            {!attendees.length ? <p className={why}>Nobody is on the attendance list yet.</p> : null}
            <p className={`${why} mt-1.5`} data-testid="hi-sign-note">{SIGN_NOTE}</p>
          </Section>

          <Section title="Assets on this inspection" count={assets.length} testId="hi-assets">
            <ResizableTable testId="hi-assets-table">
              <thead>
                <tr>
                  <ResizableTh>DISCIPLINE</ResizableTh>
                  <ResizableTh>ASSET GROUP</ResizableTh>
                  <ResizableTh>ASSET ID</ResizableTh>
                  <ResizableTh>ASSET DESCRIPTION</ResizableTh>
                  <ResizableTh>OPEN PUNCH</ResizableTh>
                  {!qc ? <ResizableTh>HANDOVER COMPLETED</ResizableTh> : null}
                  <ResizableTh>QA COMPLETE</ResizableTh>
                  <ResizableTh resizable={false} aria-label="Action" />
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => {
                  const q = qaById.get(a.element_id) || {};
                  const mineItems = (a.punch_items || []).filter((p) => p.inspection_id === (hi.inspection_id || inspectionId));
                  const open = openByCategory(mineItems);
                  const hcAt = a.handover_completed?.at;
                  const qaAt = a.qa_complete?.at || a.qa_complete?.stamped_at;
                  const gate = qc ? hi.status === 'completed' : !!hcAt;
                  return (
                    <tr key={a.element_id} className="cursor-pointer hover:bg-white/[0.02]" data-testid={`hi-asset-${a.element_id}`}
                      onClick={() => navigate(`/assets/${a.element_id}`)}>
                      <td className={TD_NOWRAP}>{q.discipline || '—'}</td>
                      <td className={TD_NOWRAP}>{q.asset_group || '—'}</td>
                      <td className={TD_NOWRAP}><span className="font-mono text-orange-500 font-bold text-[12px]">{a.asset_number || q.asset_id || a.element_id}</span></td>
                      <td className={`${TD_CLASS} text-slate-50 max-w-[60ch]`}>{a.asset_name || q.asset_name || '—'}</td>
                      <td className={TD_NOWRAP} data-testid={`hi-asset-open-${a.element_id}`}>
                        {open.length ? open.map((o, i) => (
                          <span key={o.cat}>
                            {i ? ' · ' : ''}
                            <CatChip cat={o.cat} small />{` ${o.n} open`}
                          </span>
                        )) : <span className="text-slate-500">none</span>}
                      </td>
                      {!qc ? (
                        <td className={TD_NOWRAP} data-testid={`hi-asset-hc-${a.element_id}`}>
                          {hcAt ? <span className="text-emerald-400">{hoDay(hcAt, tz)}</span>
                            : hi.status === 'completed' ? <span className="text-amber-400">{`Waiting · ${openA(a)} A open`}</span>
                              : <span className="text-slate-500">—</span>}
                        </td>
                      ) : null}
                      <td className={TD_NOWRAP} data-testid={`hi-asset-qa-${a.element_id}`}>
                        {qaAt ? <span className="text-emerald-400">{hoDay(qaAt, tz)}</span>
                          : gate ? <span className="text-amber-400">Waiting</span> : <span className="text-slate-500">—</span>}
                      </td>
                      <td className={`${TD_NOWRAP} text-right`}>
                        {canRaise ? (
                          <button type="button" className={btn.text} data-testid={`hi-raise-${a.element_id}`}
                            onClick={(e) => { e.stopPropagation(); openRaise(a.element_id); }}>+ Raise punch item</button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </ResizableTable>
            <p className="text-[11px] text-slate-500 mt-1.5 mb-0">
              {qc ? 'QA Complete: after the closeout, once the punch items are closed.'
                : 'Handover Completed: inspection completed and no Category A open. QA Complete: after Handover Completed, once B items are closed.'}
            </p>
          </Section>

          <Section
            title="Punch items raised on this inspection"
            count={hiItems.length}
            testId="hi-punch"
            right={canRaise ? (
              <button type="button" className={`${btn.outline} ${btn.sm}`} data-testid="hi-raise" onClick={() => openRaise(null)}>Raise punch item</button>
            ) : null}
          >
            {hiItems.length ? (
              <ResizableTable testId="hi-punch-table">
                <thead>
                  <tr>
                    <ResizableTh>ID</ResizableTh>
                    <ResizableTh>CAT</ResizableTh>
                    <ResizableTh>ASSET</ResizableTh>
                    <ResizableTh>DESCRIPTION</ResizableTh>
                    <ResizableTh>RAISED</ResizableTh>
                    <ResizableTh>CORRECTING</ResizableTh>
                    <ResizableTh>ACCEPTS</ResizableTh>
                    <ResizableTh resizable={false}>STATUS</ResizableTh>
                  </tr>
                </thead>
                <tbody>
                  {hiItems.map((p) => (
                    <tr key={p.id} className="cursor-pointer hover:bg-white/[0.02]" data-testid={`hi-punch-${p.id}`}
                      onClick={() => navigate(`/projects/${projectId}/handover/punch/${p.id}`)}>
                      <td className={TD_NOWRAP}><span className={btn.text}>{p.punch_number || '—'}</span></td>
                      <td className={TD_NOWRAP}><CatChip cat={p.category} /></td>
                      <td className={TD_NOWRAP}><span className="font-mono text-orange-500 font-bold text-[12px]">{p.asset_number || '—'}</span></td>
                      <td className={`${TD_CLASS} text-slate-50 max-w-[60ch]`}>
                        {p.description}
                        {lateLabel(p) ? <span className="block text-amber-400 text-[11px]">{p.raised_after_qa_complete ? 'raised after QA Complete' : 'raised after the inspection'}</span> : null}
                      </td>
                      <td className={TD_NOWRAP}>
                        {hoDay(p.raised_at, tz)}
                        <span className="block text-slate-500 text-[11px]">{[p.raised_by_name, p.raised_by_project_company_name].filter(Boolean).join(', ')}</span>
                      </td>
                      <td className={TD_NOWRAP}>{p.responsible_project_company_name || UNASSIGNED}</td>
                      <td className={TD_NOWRAP}>{p.acceptance_project_company_name || '—'}</td>
                      <td className={TD_NOWRAP}><Chip tone={p.status === 'verified' ? 'done' : 'state'}>{punchStateLabel(p.status)}</Chip></td>
                    </tr>
                  ))}
                </tbody>
              </ResizableTable>
            ) : (
              <p className={why}>
                {`No punch items yet. ${qc ? `${fromName} raises them here` : `${fromName} and ${toName} raise them here`}, with a category and a description.`}
              </p>
            )}
          </Section>

          <Section title="History" testId="hi-history" defaultOpen={false} count={history.length}>
            <ul className="list-none m-0 p-0 text-[12.5px]">
              {history.map((l, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i} className="py-1.5 border-b border-slate-800 last:border-0 text-slate-300">
                  {l.what}
                  <span className="block text-slate-500 text-[11.5px]">{[l.who, hoStamp(l.at, tz)].filter(Boolean).join(' · ')}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>

      {dialog && dialog.raise ? (
        <RaisePunchDialog
          hi={hi}
          assets={assets}
          preAsset={dialog.preAsset}
          onClose={() => setDialog(null)}
          onRaised={(created) => {
            setDialog(null);
            toast.success(`${created?.punch_number || 'Punch item'} raised`);
            if (created?.id) navigate(`/projects/${projectId}/handover/punch/${created.id}`);
            else load();
          }}
        />
      ) : null}
      {dialog && !dialog.raise ? (
        <HoDialog
          key={dialog.title}
          title={dialog.title}
          sub={dialog.sub}
          fields={dialog.fields}
          okLabel={dialog.okLabel}
          danger={dialog.danger}
          okTone={dialog.okTone}
          refusal={dialog.refusal}
          onSubmit={dialog.onSubmit}
          onClose={() => setDialog(null)}
          testId="hi-dialog"
        >
          {dialog.children}
        </HoDialog>
      ) : null}
    </Layout>
  );
}


/** The old `/execute` address of an inspection (links already sent) lands on the page. */
export function HandoverInspectionExecuteRedirect() {
  const { projectId, inspectionId } = useParams();
  return <Navigate to={`/projects/${projectId}/handover/inspections/${inspectionId}`} replace />;
}
